import React, { useState, useCallback } from 'react';
import { TemplateDashboard, TemplateEditor, TemplatePreview } from '../components/Templates';
import { EmailTemplate } from '../services/templateService';

type ViewMode = 'dashboard' | 'editor' | 'preview';

const TemplateManagement: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewMode>('dashboard');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | undefined>();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Handler to create new template
  const handleCreateTemplate = useCallback(() => {
    setSelectedTemplate(undefined);
    setCurrentView('editor');
  }, []);

  // Handler to edit existing template
  const handleEditTemplate = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    setCurrentView('editor');
  }, []);

  // Handler to preview template
  const handlePreviewTemplate = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    setCurrentView('preview');
  }, []);

  // Handler when template is saved (created or updated)
  const handleTemplateSaved = useCallback((template: EmailTemplate) => {
    console.log('Template saved:', template);
    setCurrentView('dashboard');
    setSelectedTemplate(undefined);
    // Trigger refresh of the dashboard
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Handler to return to dashboard
  const handleReturnToDashboard = useCallback(() => {
    setCurrentView('dashboard');
    setSelectedTemplate(undefined);
  }, []);

  // Render current view
  const renderCurrentView = () => {
    switch (currentView) {
      case 'editor':
        return (
          <TemplateEditor
            template={selectedTemplate}
            onSave={handleTemplateSaved}
            onCancel={handleReturnToDashboard}
          />
        );
      
      case 'preview':
        return selectedTemplate ? (
          <TemplatePreview
            template={selectedTemplate}
            onClose={handleReturnToDashboard}
            onEdit={handleEditTemplate}
          />
        ) : null;
      
      case 'dashboard':
      default:
        return (
          <TemplateDashboard
            key={refreshTrigger} // Force refresh when refreshTrigger changes
            onCreateTemplate={handleCreateTemplate}
            onEditTemplate={handleEditTemplate}
            onPreviewTemplate={handlePreviewTemplate}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {currentView === 'dashboard' ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderCurrentView()}
        </div>
      ) : (
        renderCurrentView()
      )}
    </div>
  );
};

export default TemplateManagement;